// ============================================================================
// src/modules/AccountSubpage.jsx
// -----------------------------------------------------------------------------
import React, { useState, useEffect, useMemo } from "react";
import "../styles/main.css";
import theme from "../styles/theme";
import countries from "../data/countries.json";
import {
  USER_ROLES,
  USER_STATUS,
  ACTIVITY_SECTOR_OPTIONS,
  COMPANY_SIZE_OPTIONS,
} from "../base/constants";

// --- Composants réutilisables ---
import PageHeader from "../components/reusable/ModuleHeader/PageHeader";
import Breadcrumb from "../components/reusable/ModuleHeader/Breadcrumb";
import BadgesSplitView from "../components/reusable/Navigation/BadgesSplitView";
import AccountUserManage from "../components/defined/account_manage/AccountUserManage";
import Button from "../components/reusable/Forms/Button";
import Tag from "../components/reusable/Information/Tag";
import DataGridSimple from "../components/reusable/Datagrid/DatagridSimple";
import Switch from "../components/reusable/Forms/Switch";
import TextFilter from "../components/reusable/Forms/TextFilter";
import InfoPanel from "../components/reusable/Forms/InfoPanel";
import TextField from "../components/reusable/Forms/TextField";
import DropdownList from "../components/reusable/Forms/DropdownList";
import UserAvatar from "../components/reusable/Forms/UserAvatar";
import { API_ROUTES } from "../config/api";
import{apiFetch} from "../config/apiFetch";

// ================================================================
// Helpers
// ================================================================
const normalizeEnum = (value, reference) => {
  const val = String(value ?? "").trim();
  const match = Object.values(reference).find(
    (v) => v.toLowerCase() === val.toLowerCase()
  );
  return match || Object.values(reference)[0];
};

const normalizeUser = (u) => {
  const id = u.id ?? u.Id ?? u.userId ?? u.UserId ?? null;

  return {
    ...u,
    id,
    role: normalizeEnum(u.role, USER_ROLES),
    status: normalizeEnum(u.status, USER_STATUS),
  };
};

// ================================================================
// Composant principal : Gestion des comptes
// ================================================================
const AccountSubpage = () => {
  // ==============================================================
  // States utilisateurs
  // ==============================================================
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [selectedId, setSelectedId] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [searchTerm, setSearchTerm] = useState("");
  const [showInactiveUsers, setShowInactiveUsers] = useState(false);

  // ==============================================================
  // States account
  // ==============================================================
  const [accountEditEnabled, setAccountEditEnabled] = useState(false);
  const [accountFormData, setAccountFormData] = useState({
    companyName: "",
    address: "",
    country: "",
    activitySector: "",
    companySize: "",
    firstName: "",
    lastName: "",
    phone: "",
    adminEmail: "",
    secondaryAdminEmail: "",
    secondaryFirstName: "",
    secondaryLastName: "",
  });

  // ==============================================================
  // States abonnement
  // ==============================================================
  const [abonnementSearch, setAbonnementSearch] = useState("");
  const [abonnementSwitch, setAbonnementSwitch] = useState(false);

  // ==============================================================
  // Colonnes du tableau utilisateurs
  // ==============================================================
  const columns = [
    { header: "Prénom", accessor: "firstName" },
    { header: "Nom", accessor: "lastName" },
    {
      header: "Rôle",
      accessor: "role",
      render: (row) => {
        const role = Object.values(USER_ROLES).includes(row.role)
          ? row.role
          : USER_ROLES.MEMBER;
        const color = theme.statusColors.userRole[role] || {};
        return (
          <Tag
            label={role}
            category="userRole"
            color={color.bg}
            textColor={color.text}
          />
        );
      },
    },
    { header: "Entreprise", accessor: "companyName" },
    { header: "Email", accessor: "email" },
    { header: "Téléphone", accessor: "phone" },
    {
      header: "Statut",
      accessor: "status",
      render: (row) => {
        const status = Object.values(USER_STATUS).includes(row.status)
          ? row.status
          : USER_STATUS.ACTIVE;
        const color = theme.statusColors.userStatus[status] || {};
        return (
          <Tag
            label={status}
            category="userStatus"
            color={color.bg}
            textColor={color.text}
          />
        );
      },
    },
  ];

  // ==============================================================
  // Chargement utilisateurs
  // ==============================================================
  useEffect(() => {
    const ac = new AbortController();

    const loadUsers = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await apiFetch(API_ROUTES.users.getAll, { signal: ac.signal });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const list = await res.json();
        const normalized = (Array.isArray(list) ? list : []).map(normalizeUser);

        if (!ac.signal.aborted) {
          setUsers(normalized);
        }
      } catch (err) {
        if (!ac.signal.aborted) {
          console.error(err);
          setError("Erreur de chargement des utilisateurs");
        }
      } finally {
        if (!ac.signal.aborted) {
          setLoading(false);
        }
      }
    };

    loadUsers();
    return () => ac.abort();
  }, []);

  // ==============================================================
  // Chargement Account
  // ==============================================================
  useEffect(() => {
    const loadAccountData = async () => {
      try {
        const res = await apiFetch(API_ROUTES.accountProfile.get);

        if (!res.ok) throw new Error(`HTTP ${res.status}`);

        const data = await res.json();

        if (data) {
          setAccountFormData((prev) => ({ ...prev, ...data }));
        }
      } catch (err) {
        console.error("Erreur chargement account:", err);
      }
    };

    loadAccountData();
  }, []);

  // ==============================================================
  // Actions utilisateurs
  // ==============================================================
  const openCreate = () => {
    setIsEditMode(false);
    setIsModalOpen(true);
    setSelectedId(null);
    setSelectedRow(null);
  };

  const openEdit = () => {
    if (!selectedRow) return;
    setIsEditMode(true);
    setIsModalOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedId) return;

    try {
      const res = await apiFetch(API_ROUTES.users.byId(selectedId), {
        method: "DELETE",
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      setUsers((prev) => prev.filter((u) => u.id !== selectedId));
      setSelectedId(null);
      setSelectedRow(null);
    } catch (err) {
      console.error(err);
      alert("Suppression impossible");
    }
  };

  // ==============================================================
  // Actions account
  // ==============================================================
  const handleAccountChange = (field, value) => {
    setAccountFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleAccountSave = async () => {
    try {
      const res = await apiFetch(API_ROUTES.accountProfile.update, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(accountFormData),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      console.log("Account info saved successfully");
    } catch (err) {
      console.error("Save error:", err);
    }
  };

  // ==============================================================
  // Filtrage utilisateurs
  // ==============================================================
  const filteredUsers = users
    .filter((u) =>
      [u.firstName, u.lastName, u.email, u.companyName]
        .filter(Boolean)
        .some((field) =>
          String(field).toLowerCase().includes(searchTerm.trim().toLowerCase())
        )
    )
    .filter((u) => {
      const inactiveStatuses = [USER_STATUS.INACTIVE, USER_STATUS.SUSPENDED];
      if (!showInactiveUsers && inactiveStatuses.includes(u.status)) {
        return false;
      }
      return true;
    });

  // ==============================================================
  // Onglet Users
  // ==============================================================
  const UsersTab = (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: theme.spacing.md,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing.md,
            marginLeft: "25px",
          }}
        >
          <Button variant="blue" onClick={openCreate}>
            Create
          </Button>
          <Button variant="blue" onClick={openEdit} disabled={!selectedId}>
            Edit
          </Button>
          <Button variant="red" onClick={handleDelete} disabled={!selectedId}>
            Delete
          </Button>
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: theme.spacing.md,
            width: "600px",
          }}
        >
          <TextFilter
            label="Recherche"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Rechercher un utilisateur"
          />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            lineHeight: 1,
            marginRight: "25px",
          }}
        >
          <Switch
            label="Membres inactifs"
            enabled={showInactiveUsers}
            onChange={setShowInactiveUsers}
            showText={false}
            size="small"
            switchStyle={{ transform: "translateY(2px)" }}
          />
        </div>
      </div>

      {loading && <p className="secondary">Chargement…</p>}
      {error && <p className="muted">{error}</p>}

      <DataGridSimple
        columns={columns}
        data={filteredUsers}
        selectable
        selectedId={selectedId}
        onSelectionChange={(id, row) => {
          if (!id || id === selectedId) {
            setSelectedId(null);
            setSelectedRow(null);
            return;
          }
          setSelectedId(id);
          setSelectedRow(row);
        }}
      />
    </div>
  );

  // ==============================================================
  // Onglet Account
  // ==============================================================
  const AccountTab = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.lg,
          marginTop: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: theme.spacing.md,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing.md,
              marginLeft: 25,
            }}
          >
            {/* // désactivation momentannée, à activer uniquement si l’API supporte la mise à jour du compte */}
            {/* <Button variant="blue" onClick={handleAccountSave}>
              Enregistrer
            </Button> */}
            <Button variant="blue" disabled>
              Enregistrer
            </Button>



          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              marginRight: 25,
            }}
          >
            <Switch
              label="Modifier les données"
              // désactivation momentannée, à activer uniquement si l’API supporte la mise à jour du compte
              enabled={false}
              onChange={() => {}}
              disabled
                          
             /*  enabled={accountEditEnabled}
              onChange={setAccountEditEnabled} */
              showText={false}
              size="small"
              switchStyle={{ transform: "translateY(2px)" }}
            />
          </div>
        </div>

        <div
          style={{
            display: "flex",
            gap: 120,
            marginLeft: 120,
            marginRight: 120,
            minWidth: 1600,
            overflowX: "auto",
            flexWrap: "nowrap",
          }}
        >
          <div
            style={{
              flex: 1,
              minWidth: 180,
              minHeight: 600,
              background: theme.colors.background.form,
              borderRadius: theme.borders.radius.sm,
              padding: theme.spacing.lg,
              boxShadow: theme.shadows.sm,
              border: `1px solid ${theme.colors.borders.panel}`,
              flexShrink: 0,
              overflowX: "auto",
            }}
          >
            <h2
              style={{
                marginBottom: theme.spacing.md,
                color: theme.colors.text.default,
              }}
            >
              Informations de l’entreprise
            </h2>

            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: theme.spacing.md,
                width: "100%",
              }}
            >
              <TextField
                label="Nom de la société"
                value={accountFormData.companyName}
                onChange={(e) =>
                  handleAccountChange("companyName", e.target.value)
                }
                disabled={!accountEditEnabled}
              />

              <TextField
                label="Adresse postale"
                value={accountFormData.address}
                onChange={(e) => handleAccountChange("address", e.target.value)}
                placeholder="Entrez l'adresse"
                disabled={!accountEditEnabled}
              />

              <DropdownList
                label="Pays"
                options={countries}
                value={accountFormData.country}
                onChange={(value) => handleAccountChange("country", value)}
                style={{ width: "25%", marginBottom: theme.spacing.lg }}
                disabled={!accountEditEnabled}
              />

              <div
                style={{
                  display: "flex",
                  gap: theme.spacing.xs,
                  width: "100%",
                }}
              >
                <DropdownList
                  label="Secteur d’activité"
                  options={ACTIVITY_SECTOR_OPTIONS}
                  value={accountFormData.activitySector}
                  onChange={(value) =>
                    handleAccountChange("activitySector", value)
                  }
                  style={{ height: "100%", width: 450 }}
                  disabled={!accountEditEnabled}
                />

                <DropdownList
                  label="Taille de la société"
                  options={COMPANY_SIZE_OPTIONS}
                  value={accountFormData.companySize}
                  onChange={(value) =>
                    handleAccountChange("companySize", value)
                  }
                  style={{ flex: 1, height: "100%", marginBottom: 30 }}
                  disabled={!accountEditEnabled}
                />
              </div>

              <InfoPanel
                title="Licence Freemium activée"
                message="Vous bénéficiez actuellement d’une période d’évaluation de 7 jours. Certaines fonctionnalités avancées seront disponibles après l’activation d’une licence complète."
                variant="yellow"
                stripPosition="left"
                border={true}
                rounded={true}
              />
            </div>
          </div>

          <div
            style={{
              flex: 1,
              minWidth: 180,
              minHeight: 600,
              background: theme.colors.background.form,
              borderRadius: theme.borders.radius.sm,
              padding: theme.spacing.lg,
              boxShadow: theme.shadows.sm,
              border: `1px solid ${theme.colors.borders.panel}`,
              flexShrink: 0,
              overflowX: "auto",
            }}
          >
            <h2
              style={{
                marginBottom: theme.spacing.md,
                color: theme.colors.text.default,
              }}
            >
              Administrateur principal
            </h2>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.md,
                width: "100%",
              }}
            >
              <TextField
                label="Identifiant (email user)"
                value={accountFormData.adminEmail}
                onChange={(e) =>
                  handleAccountChange("adminEmail", e.target.value)
                }
                style={{ width: "80%" }}
                disabled={!accountEditEnabled}
              />
              <UserAvatar name="Errazak Saïd" color="magenta" size={52} />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.md,
                width: "100%",
              }}
            >
              <TextField
                label="Prénom"
                value={accountFormData.firstName}
                onChange={(e) =>
                  handleAccountChange("firstName", e.target.value)
                }
                style={{ flex: "0 0 350px" }}
                disabled={!accountEditEnabled}
              />

              <TextField
                label="Nom"
                value={accountFormData.lastName}
                onChange={(e) =>
                  handleAccountChange("lastName", e.target.value)
                }
                style={{ flex: "0 0 350px" }}
                disabled={!accountEditEnabled}
              />
            </div>

            <h2
              style={{
                marginBottom: theme.spacing.md,
                color: theme.colors.text.default,
              }}
            >
              Administrateur secondaire
            </h2>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.md,
                width: "100%",
              }}
            >
              <TextField
                label="Identifiant (email user)"
                value={accountFormData.secondaryAdminEmail}
                onChange={(e) =>
                  handleAccountChange("secondaryAdminEmail", e.target.value)
                }
                style={{ width: "80%" }}
                disabled={!accountEditEnabled}
              />
              <UserAvatar name="Jean Dupont" color="magenta" size={52} />
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "flex-start",
                gap: theme.spacing.xs,
                marginBottom: theme.spacing.md,
                width: "100%",
              }}
            >
              <TextField
                label="Prénom"
                value={accountFormData.secondaryFirstName}
                onChange={(e) =>
                  handleAccountChange("secondaryFirstName", e.target.value)
                }
                style={{ flex: "0 0 350px" }}
                disabled={!accountEditEnabled}
              />

              <TextField
                label="Nom"
                value={accountFormData.secondaryLastName}
                onChange={(e) =>
                  handleAccountChange("secondaryLastName", e.target.value)
                }
                style={{ flex: "0 0 350px" }}
                disabled={!accountEditEnabled}
              />
            </div>
          </div>
        </div>
      </div>
    ),
    [accountFormData, accountEditEnabled]
  );

  // ==============================================================
  // Onglet Abonnement
  // ==============================================================
  const AbonnementTab = useMemo(
    () => (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: theme.spacing.lg,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: theme.spacing.sm,
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing.md,
              marginLeft: "25px",
            }}
          >
            <Button variant="blue">Upgrade</Button>
            <Button variant="red">Cancel</Button>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing.md,
              width: "600px",
            }}
          >
            <TextFilter
              label="Recherche"
              value={abonnementSearch}
              onChange={(e) => setAbonnementSearch(e.target.value)}
              placeholder="Rechercher un abonnement"
            />
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              lineHeight: 1,
              marginRight: "25px",
            }}
          >
            <Switch
              label="Abonnement actif"
              enabled={abonnementSwitch}
              onChange={setAbonnementSwitch}
              showText={false}
              size="small"
              switchStyle={{ transform: "translateY(2px)" }}
            />
          </div>
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 300,
            borderRadius: theme.borders.radius.lg,
            padding: theme.spacing.lg,
            boxShadow: theme.shadows.sm,
            marginLeft: 120,
            marginRight: 120,
          }}
        >
          <h3
            style={{
              marginBottom: theme.spacing.md,
              color: theme.colors.text.primary,
            }}
          >
            Gestion des abonnements
          </h3>
          <p className="secondary">
            Ici, vous pourrez gérer le plan, la facturation et les options
            d’abonnement.
          </p>
        </div>
      </div>
    ),
    [abonnementSearch, abonnementSwitch]
  );

  // ==============================================================
  // Tabs
  // ==============================================================
  const tabs = useMemo(
    () => [
      { label: "Users", content: UsersTab },
      { label: "Account", content: AccountTab },
      { label: "Abonnement", content: AbonnementTab },
    ],
    [UsersTab, AccountTab, AbonnementTab]
  );

  // ==============================================================
  // Rendu
  // ==============================================================
  return (
    <div style={{ padding: 30 }}>
      <Breadcrumb items={[{ label: "Account", href: "/" }]} />
      <PageHeader
        title="Gestion des comptes"
        subtitle="Administration des utilisateurs et permissions"
      />

      <div
        style={{
          marginTop: 24,
          marginBottom: 28,
          padding: theme.spacing.md,
          borderRadius: theme.borders.radius.md,
        }}
      >
        <BadgesSplitView
          tabs={tabs}
          containerStyle={{ background: theme.colors.background.app }}
        />
      </div>

      <AccountUserManage
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        mode={isEditMode ? "edit" : "create"}
        initialValues={isEditMode ? selectedRow : null}
        onSave={(savedUser) => {
          const normalizedUser = normalizeUser(savedUser);

          setUsers((prev) => {
            const idx = prev.findIndex((u) => u.id === normalizedUser.id);
            if (idx === -1) return [normalizedUser, ...prev];

            const next = [...prev];
            next[idx] = normalizedUser;
            return next;
          });
        }}
      />
    </div>
  );
};

export default AccountSubpage;